<script lang="ts" setup>
import { AppButton, AppConfirmDialog, AppTypography } from '@/components/base';
import { JobListingDetails } from '@/components/support-seeker';
import { JobListingUploadDocuments } from '@/components/support-seeker/forms';
import type { JobListing } from '@/types/JobListing';
import { useAuthStore } from '@/modules/auth/store/auth';
import { extractApiErrorMessage, logApiError } from '@/utils/errorHandler';

const props = withDefaults(
  defineProps<{
    jobListing?: JobListing;
  }>(),
  {}
);

const modal = defineModel<boolean>();
const showConfirm = ref(false);
const showSuccess = ref(false);
const isApplying = ref(false);
const isUploading = ref(false);
const isApplied = ref(false); // Add this to store the is_applied value
const { showSnackbar } = useSnackbar();
const { fetching, jobListing: jobListingRequirements, findJobListing } = useJobListing();
const { data, error: serverError, execute: execCreateJob, pending: loading } = useSubmit();

// Using the utility function from errorHandler.ts
const route = useRoute();
const activeTab = computed(() => route.query.tab);
const showVerificationPrompt = ref(false);

const handleObscuredClick = (): void => {
  if (!isFullyVerified) {
    showVerificationPrompt.value = true;
  }
};

const hasAllDocuments = computed((): number => {
  const count = jobListingRequirements.value?.required_docs_and_qualification || 0;

  return (
    (jobListingRequirements.value?.required_docs_and_qualification || []).filter((doc) => !!doc.current_document)
      .length || 0 >= count
  );
});

const hasMatchingDocuments = computed((): number => {
  const count = jobListingRequirements.value?.required_docs_and_qualification || 0;

  return (
    (jobListingRequirements.value?.required_docs_and_qualification || []).filter(
      (doc) => !!doc.current_document?.approved_at
    ).length || 0 >= count
  );
});

watch(
  () => modal.value,
  (value) => {
    if (value) {
      reset();
    }

    if (!!value && !!props.jobListing?.id) {
      fetch();
    }
  }
);

const submit = (): void => {
  if (hasMatchingDocuments.value) {
    handleSubmit();
  } else {
    toggleConfirm(true);
  }
};

const handleSubmit = (): Promise<void> => {
  // TODO

  cancel();
  reset();

  toggleSuccess(true);
};

const fetch = async (): Promise<void> => {
  await findJobListing(props.jobListing?.id);

  if (!jobListingRequirements.value) {
    cancel();
  }
};

const applyForJob = async (jobId: string): Promise<void> => {
  try {
    await execCreateJob('jobs/request-contact', {
      method: 'POST',
      body: {
        job_id: jobId,
      },
    });
        
    if (data.value) {
      showSnackbar({
        text: data.value.data.message || 'Application submitted successfully!',
        state: 'success',
      });
    }
    modal.value = false;

  } catch (error) {
    // Log error details for debugging
    logApiError(serverError.value || error, 'Job Application Error');
    
    // Extract the actual API error message
    const errorMessage = extractApiErrorMessage(
      serverError.value || error, 
      'Failed to submit application. Please try again.'
    );
    
    showSnackbar({
      text: errorMessage,
      state: 'error',
    });
  }
};

const toggleApplying = (bool: boolean): void => {
  isApplying.value = bool;
};

const toggleUploading = (bool: boolean): void => {
  isUploading.value = bool;
};

const toggleConfirm = (bool: boolean): void => {
  showConfirm.value = bool;
};

const toggleSuccess = (bool: boolean): void => {
  showSuccess.value = bool;
};

const handleIsApplied = (value: boolean): void => {
  console.log('Received isApplied value from JobListingDetails:', value);
  isApplied.value = value;
};

const cancel = (): void => {
  jobListingRequirements.value = null;
  modal.value = false;
};

const reset = (): void => {
  toggleApplying(false);
  toggleUploading(false);
  toggleConfirm(false);
};

const { isFullyVerified } = useAuthStore();

onMounted(() => {
  reset();
});
</script>
<template>
  <v-dialog v-model="modal" :max-width="600">
    <v-card>
      <v-card-text>
        <template v-if="!isApplying">
          <template v-if="!fetching">
            <JobListingDetails 
              :jobListingId="jobListing?.id" 
              :jobListing="jobListing" 
              :isFullyVerified="isFullyVerified" 
              @isApplied="handleIsApplied"
            />
            
            <!-- Show application status -->
            <AppButton 
              v-if="isApplied || jobListing.is_applied" 
              variant="tonal" 
              block 
              class="mb-4" 
              disabled 
            >
              Applied
            </AppButton>
            
            <AppButton 
              v-if="activeTab != 'applied' && activeTab != 'accepted' && !isApplied && !jobListing.is_applied" 
              variant="tonal" 
              block 
              class="mb-4" 
              @click="!isFullyVerified ? handleObscuredClick() : applyForJob(jobListing?.job_id ? jobListing?.job_id : jobListing?.id)"
            > 
              Apply for job
            </AppButton>
          </template>
          <template v-else>
            <v-skeleton-loader type="article" />
          </template>

          <AppButton variant="text" block @click="cancel">Cancel</AppButton>
        </template>
        <template v-else>
          <JobListingUploadDocuments
            :required-documents="jobListingRequirements?.required_docs_and_qualification"
            class="pt-5"
            @cancel="toggleApplying(false)"
            @uploaded="fetch"
            @uploading="toggleUploading($event)"
          />

          <template v-if="!isUploading">
            <AppButton :disabled="!hasAllDocuments" block class="mb-4" @click="submit">Continue</AppButton>
            <AppButton variant="text" block @click="toggleApplying(false)">Cancel</AppButton>
          </template>
        </template>
      </v-card-text>
      <div v-if="showVerificationPrompt" class="modal-overlay" @click.self="showVerificationPrompt = false">
        <div class="modal-content">
          <span class="modal-close" @click="showVerificationPrompt = false" aria-label="Close">&times;</span>
          <div class="modal-message">
            Please complete your profile to see jobs suited to you.
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>

  <AppConfirmDialog
    v-model="showConfirm"
    title="Documents need to be verified"
    description="Your documents are subject to verification by SupportApp. If any of your documents are rejected, your application will also be rejected. Make sure you're submitting the right documents."
    confirm-text="Confirm"
    @confirm="handleSubmit"
    @cancel="toggleConfirm(false)"
  />

  <AppConfirmDialog
    v-model="showSuccess"
    title=""
    description=""
    confirm-text="Continue"
    cancel-text=""
    @confirm="toggleSuccess(false)"
  >
    <template #description>
      <AppTypography type="headline-1">You have successfully applied for this job!</AppTypography>
    </template>
  </AppConfirmDialog>

</template>
<style lang="scss" scoped>

// Modal styles
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 450px;
  width: 90%;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.3s ease-out;
}

.modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  line-height: 1;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
}

.modal-message {
  font-size: 15px;
  color: #333;
  text-align: center;
  margin-top: 18px;
  line-height: 1.5;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

</style>
